import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Select } from "risa-oasis-ui_v2";
import { LoaderMessage } from "../../../../components/loaderMessage/loaderMessage";
import { ToastMessages } from "../../../../components/toasts/toastMessages";
import { fetchHealthFacilityFromFirebase } from "../../../../redux/slice/healthFacilitySlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import AuthOptionsSection from "./components/authOptionsSection/authOptionsSection";
import BoOptionsSection from "./components/boOptionsSection/boOptionsSection";
import { useAuthOptions } from "./hooks/useAuthOptions";
import { useBoOptions } from "./hooks/useBoOptions";

const OptionsConfiguration = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { data: healthFacilityData, loading: facilitiesLoading } = useSelector(
    (state: RootState) => state.healthFacility,
  );

  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [saving, setSaving] = useState(false);

  const boOptions = useBoOptions(selectedOrgId);
  const authOptions = useAuthOptions(selectedOrgId);

  const hasUnsavedChanges =
    boOptions.hasUnsavedChanges || authOptions.hasUnsavedChanges;

  useEffect(() => {
    dispatch(fetchHealthFacilityFromFirebase());
  }, [dispatch]);

  const organizationOptions =
    healthFacilityData?.map((facility) => ({
      label: facility.Name ?? "",
      value: facility.DocId ?? "",
    })) ?? [];

  const handleOrganizationChange = (option: { value: string }) => {
    setSelectedOrgId(option.value);
  };

  const handleSaveAllChanges = async () => {
    setSaving(true);
    try {
      const promises: Promise<void>[] = [];
      if (boOptions.hasUnsavedChanges) {
        promises.push(boOptions.handleSaveReorder());
      }
      if (authOptions.hasUnsavedChanges) {
        promises.push(authOptions.handleSaveReorder());
      }
      await Promise.all(promises);
      toast.success(ToastMessages.OPTION_SAVE_ALL_SUCCESS);
    } catch (error) {
      console.error("Error saving all changes:", error);
      toast.error(ToastMessages.OPTION_SAVE_ALL_ERROR);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscardAllChanges = () => {
    boOptions.handleDiscardReorder();
    authOptions.handleDiscardReorder();
  };

  const isLoading =
    boOptions.loading || authOptions.loading || facilitiesLoading;

  return (
    <div className="flex h-full flex-col gap-4 overflow-hidden">
      {/* Header with Organization Selector */}
      <div className="flex items-center justify-between border-b border-gray-200 pb-2">
        <div className="text-base font-semibold">Options Configuration</div>
        <div className="w-[30%]">
          <Select
            id="organization"
            options={organizationOptions}
            label=""
            placeholder="Select Organization"
            onOptionChange={handleOrganizationChange}
            defaultValue={selectedOrgId}
          />
        </div>
      </div>

      {!selectedOrgId ? (
        <div className="flex h-40 items-center justify-center text-center text-sm text-gray-500">
          Please select an organization to view and manage options.
        </div>
      ) : isLoading ? (
        <div className="flex-1">
          <LoaderMessage message={"Loading options..."} />
        </div>
      ) : (
        <>
          <div className="flex flex-1 gap-4 overflow-hidden">
            <BoOptionsSection
              sortedOptionsList={boOptions.sortedOptionsList}
              optionsList={boOptions.optionsList}
              draggedIndex={boOptions.draggedIndex}
              showModal={boOptions.showModal}
              modalMode={boOptions.modalMode}
              editingOption={boOptions.editingOption}
              showDeleteModal={boOptions.showDeleteModal}
              deletingOption={boOptions.deletingOption}
              organizationOptions={organizationOptions}
              selectedOrgId={selectedOrgId}
              onAdd={boOptions.handleAdd}
              onEdit={boOptions.handleEdit}
              onDeleteClick={boOptions.handleDeleteClick}
              onModalClose={boOptions.handleModalClose}
              onSave={boOptions.handleSave}
              onDeleteConfirm={boOptions.handleDeleteConfirm}
              onDeleteCancel={boOptions.handleDeleteCancel}
              onCopyFromOrg={boOptions.handleCopyFromOrg}
              onDragStart={boOptions.handleDragStart}
              onDragOver={boOptions.handleDragOver}
              onDragEnd={boOptions.handleDragEnd}
              onDrop={boOptions.handleDrop}
            />

            {/* Divider */}
            <div className="w-px bg-gray-200" />

            <AuthOptionsSection
              sortedOptionsList={authOptions.sortedOptionsList}
              optionsList={authOptions.optionsList}
              draggedIndex={authOptions.draggedIndex}
              sortOption={authOptions.sortOption}
              showModal={authOptions.showModal}
              modalMode={authOptions.modalMode}
              editingOption={authOptions.editingOption}
              showDeleteModal={authOptions.showDeleteModal}
              deletingOption={authOptions.deletingOption}
              showReorderModal={authOptions.showReorderModal}
              organizationOptions={organizationOptions}
              selectedOrgId={selectedOrgId}
              boOptions={boOptions.optionsList}
              onAdd={authOptions.handleAdd}
              onEdit={authOptions.handleEdit}
              onDeleteClick={authOptions.handleDeleteClick}
              onModalClose={authOptions.handleModalClose}
              onSave={authOptions.handleSave}
              onDeleteConfirm={authOptions.handleDeleteConfirm}
              onDeleteCancel={authOptions.handleDeleteCancel}
              onCopyFromOrg={authOptions.handleCopyFromOrg}
              onReorderClick={authOptions.handleReorderClick}
              onReorderModalClose={authOptions.handleReorderModalClose}
              onReorderSave={authOptions.handleReorderSave}
              onDragStart={authOptions.handleDragStart}
              onDragOver={authOptions.handleDragOver}
              onDragEnd={authOptions.handleDragEnd}
              onDrop={authOptions.handleDrop}
            />
          </div>

          {/* Save Changes Bar */}
          {hasUnsavedChanges && (
            <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="h-2 w-2 animate-pulse rounded-full bg-blue-500" />
                <span className="text-sm font-medium text-gray-700">
                  You have unsaved reorder changes
                </span>
                <span className="text-sm text-gray-500">
                  {boOptions.hasUnsavedChanges && authOptions.hasUnsavedChanges
                    ? "(BO Options & Auth Options)"
                    : boOptions.hasUnsavedChanges
                      ? "(BO Options)"
                      : "(Auth Options)"}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <button
                  disabled={saving}
                  onClick={handleDiscardAllChanges}
                  className="rounded-md border border-gray-300 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:border-gray-400 hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Discard Changes
                </button>
                <Button
                  disabled={saving}
                  children={saving ? "Saving..." : "Save All Changes"}
                  onClick={handleSaveAllChanges}
                  buttonType={"primary"}
                  size={"small"}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OptionsConfiguration;
